import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InventoryIcon from '@mui/icons-material/Inventory';
import { PackingOperation } from '../schema';
import { queryItemTypesWithLocations } from '../schema';
import { filterValidItemLocations } from '../utils/instantDbGuards';

interface Props {
    operation: PackingOperation;
    open: boolean;
    onClose: () => void;
}

const DRAWER_WIDTH = 320;

export default function CurrentInventoryPanel({ operation, open, onClose }: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { data: itemTypesData } = queryItemTypesWithLocations();

    const sourceEventIds = new Set((operation.sourceEvents || []).map((e: { id: string }) => e.id));
    const targetEventIds = new Set((operation.targetEvents || []).map((e: { id: string }) => e.id));
    const sourceStorageIds = new Set((operation.sourceStorageLocations || []).map((s: { id: string }) => s.id));
    const targetStorageIds = new Set((operation.targetStorageLocations || []).map((s: { id: string }) => s.id));

    const inventoryByLocation = new Map<
        string,
        Map<string, { itemTypeId: string; itemTypeName: string; quantity: number; categoryName: string }>
    >();

    if (itemTypesData?.itemTypes) {
        const itemTypes = itemTypesData.itemTypes as Array<{
            id: string;
            name: string;
            category?: { name?: string } | null;
            itemLocations?: Array<{
                event?: { id: string } | null;
                storageLocation?: { id: string } | null;
                quantity?: number;
            }>;
        }>;
        for (const itemType of itemTypes) {
            const categoryName = itemType.category?.name ?? 'Uncategorized';
            const itemLocations = filterValidItemLocations(itemType.itemLocations || []);
            for (const location of itemLocations) {
                const quantity = location.quantity || 0;
                const eventId = location.event?.id ?? null;
                const storageId = location.storageLocation?.id ?? null;
                const locationKey = eventId ?? storageId;
                if (!locationKey) continue;
                const isRelevant =
                    (eventId && (sourceEventIds.has(eventId) || targetEventIds.has(eventId))) ||
                    (storageId && (sourceStorageIds.has(storageId) || targetStorageIds.has(storageId)));
                if (!isRelevant) continue;
                if (!inventoryByLocation.has(locationKey)) {
                    inventoryByLocation.set(locationKey, new Map());
                }
                const byLoc = inventoryByLocation.get(locationKey);
                if (byLoc === undefined) throw new Error('CurrentInventoryPanel: invariant — map row missing');
                byLoc.set(itemType.id, {
                    itemTypeId: itemType.id,
                    itemTypeName: itemType.name,
                    quantity,
                    categoryName,
                });
            }
        }
    }

    const getLocationName = (locationKey: string): string => {
        const storage = [...(operation.sourceStorageLocations || []), ...(operation.targetStorageLocations || [])].find(
            (s: { id: string }) => s.id === locationKey
        );
        if (storage) return (storage as { name?: string }).name ?? 'Storage';
        const event = [...(operation.sourceEvents || []), ...(operation.targetEvents || [])].find(
            (e: { id?: string }) => e.id === locationKey
        );
        return (event as { name?: string })?.name ?? 'Unknown Event';
    };

    const _getTrailerNamesForEvent = (eventId: string): string[] => {
        const event = [...(operation.sourceEvents || []), ...(operation.targetEvents || [])].find((e: { id?: string }) => e.id === eventId);
        const items =
            (event as { packingListItems?: Array<{ itemType?: { name?: string; category?: { name?: string } } }> })?.packingListItems || [];
        const seen = new Set<string>();
        return items
            .filter(item => item.itemType?.category?.name === 'Trailers')
            .map(item => item.itemType?.name)
            .filter((name): name is string => {
                if (!name || seen.has(name)) return false;
                seen.add(name);
                return true;
            });
    };

    const content = (
        <Box sx={{ width: isMobile ? '100%' : DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InventoryIcon />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Current Inventory
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2 }}>
                {inventoryByLocation.size === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No inventory data available
                    </Typography>
                ) : (
                    <>
                        {/* Storage locations and events */}
                        {Array.from(inventoryByLocation.keys())
                            .sort((a, b) => getLocationName(a).localeCompare(getLocationName(b)))
                            .map(locationKey => {
                                const locationName = getLocationName(locationKey);
                                const items = inventoryByLocation.get(locationKey);
                                if (!items) return null;
                                return (
                                    <Accordion key={locationKey} defaultExpanded>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {locationName}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {(() => {
                                                const itemList = Array.from(items.values()).sort((a, b) =>
                                                    a.itemTypeName.localeCompare(b.itemTypeName)
                                                );
                                                const byCategory = new Map<string, typeof itemList>();
                                                for (const item of itemList) {
                                                    const list = byCategory.get(item.categoryName) ?? [];
                                                    list.push(item);
                                                    byCategory.set(item.categoryName, list);
                                                }
                                                const categoryNames = Array.from(byCategory.keys()).sort((a, b) => a.localeCompare(b));
                                                return categoryNames.map(categoryName => (
                                                    <Box key={categoryName} sx={{ mb: 2 }}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            color="text.secondary"
                                                            sx={{ py: 0.5, fontWeight: 600 }}
                                                        >
                                                            {categoryName}
                                                        </Typography>
                                                        <List dense>
                                                            {(byCategory.get(categoryName) ?? []).map(item => (
                                                                <ListItem key={item.itemTypeId} disablePadding>
                                                                    <ListItemText
                                                                        primary={item.itemTypeName}
                                                                        secondary={`${item.quantity} items`}
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>
                                                ));
                                            })()}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                    </>
                )}
            </Box>
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer anchor="bottom" open={open} onClose={onClose} PaperProps={{ sx: { maxHeight: '90vh' } }}>
                {content}
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                },
            }}
        >
            {content}
        </Drawer>
    );
}
